import React, { useMemo, useState } from 'react';
import { useIntl, history, useParams } from 'umi';
import { inject, observer } from 'mobx-react';
import RTCModel from '@/models/rtc';
import MeetingModel from '@/models/meeting';
import UserModel from '@/models/user';
import { Icon, message } from '@/components';
import Style from './index.less';
import Check from '../Check';
import ReadyH5 from './H5';
import { FeedbackPanelPopover } from '@/components/FeedbackPanel';
import LoginInput from './component/LoginInput';
import { TIPS_TYPE } from '@/types/tips';
import { clearSessionStorage, getQueryString } from '@/utils/common';
import { LOGIN_TYPE } from './component/LoginHeader';
import { get } from 'lodash-es';
import { setLocalPassword } from '@/utils/localStorage';
import { elkReport } from '@/utils/elkReport';

interface ReadyProps {
  RTCInstance?: RTCModel;
  meeting?: MeetingModel;
  user?: UserModel;
}

interface ParamsStruct {
  meetingId: string;
}

const Ready: React.FC<ReadyProps> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
    user: store.user,
  };
})(
  observer((props) => {
    const { RTCInstance, meeting, user } = props;
    const { formatMessage } = useIntl();
    const { meetingId } = useParams<ParamsStruct>();
    const [currentAvatarId] = React.useState<number>(0);
    const [btnLoading, setBtnLoading] = React.useState<boolean>(false);
    const [isMICChecking, setIsMICChecking] = React.useState<boolean>(false);
    const [isAudioOpen, setIsAudioOpen] = React.useState<boolean>(false);
    const mustCheck = getQueryString('mustcheck') === '1';
    const [inputName, setInputName] = useState('');
    const [firstMemberNotifyOn, setFirstMemberNotifyOn] = useState(false);

    const onMicSwitchChange = React.useCallback(
      async (value: boolean) => {
        value ? RTCInstance?.openMic(false) : RTCInstance?.closeMic();
        setIsAudioOpen(value);
      },
      [RTCInstance]
    );

    const onInputChange = (value: string) => {
      setInputName(value);
    };
    const isGuest = useMemo(() => {
      const loginGuest =
        get(meeting, 'info?.supportedAuthTypes.0') === LOGIN_TYPE.GUEST &&
        meeting?.info?.supportedAuthTypes?.length === 1;
      return window.location.href.includes(`${LOGIN_TYPE.GUEST}=1`) || loginGuest;
    }, [meeting?.info?.supportedAuthTypes]);

    React.useEffect(() => {
      isAudioOpen ? RTCInstance?.openMic(false) : RTCInstance?.closeMic();
    }, [isMICChecking]);

    const checkMIC = React.useCallback(async () => {
      if (!user?.isOnline) {
        message.info({
          content: formatMessage({ id: 'OFFLINE' }),
          noIcon: true,
        });
        return;
      }
      if (
        !(RTCInstance?.deviceOptions.audioIn && RTCInstance?.deviceOptions.audioIn.length > 0) ||
        !(RTCInstance?.deviceOptions.audioOut && RTCInstance?.deviceOptions.audioOut.length > 0)
      ) {
        message.warning({
          content: formatMessage({ id: 'NO_DEVICE' }),
        });
        return;
      }
      if (!RTCInstance?.isOpenAudio) {
        await RTCInstance?.openMic(false).catch(() => {
          message.warning({
            content: formatMessage({ id: 'DIVIECE_DISABLE_TITLE' }),
          });
        });
      }
      setIsMICChecking(true);
    }, [user, RTCInstance?.deviceOptions, RTCInstance, RTCInstance?.isOpenAudio]);

    const handleCloseMICCheck = React.useCallback(async () => {
      setIsMICChecking(false);
    }, []);

    const joinMeeting = React.useCallback(
      async (name: string, password?: string) => {
        if (!user?.isOnline) {
          message.info({
            content: formatMessage({ id: 'OFFLINE' }),
            noIcon: true,
          });
          return;
        }
        // if (!name.trim()) {
        //   setInputValue(name.trim());
        //   return;
        // }
        try {
          setBtnLoading(true);
          let authType;
          if (user?.user?.openAuthed && user?.user?.authType === LOGIN_TYPE.NEOpenID) {
            authType = LOGIN_TYPE.NEOpenID;
          } else if (user?.user?.openAuthed && user?.user?.authType === LOGIN_TYPE.Oscar) {
            authType = LOGIN_TYPE.Oscar;
          } else {
            authType = LOGIN_TYPE.GUEST;
          }
          if (window.isMobile) {
            authType = LOGIN_TYPE.GUEST;
          }

          const resp = await user?.login(
            authType,
            name,
            meetingId,
            currentAvatarId,
            getQueryString('pwd')
          );
          window.sessionStorage.setItem('waterMark', JSON.stringify(resp?.data?.watermark));
          await meeting?.getInfo(
            {
              meetingId,
              scheduleSt: (history.location.query?.scheduleSt as string) || '',
              firstMemberNotifyOn: firstMemberNotifyOn ? '1' : '0',
              password,
            },
            true
          );
          password && setLocalPassword(meetingId, password);
          const search = new URLSearchParams(window.location.search);
          const nextSearch = new URLSearchParams();
          for (const [key, value] of search.entries()) {
            if (['isRecruitMeeting', 'source'].includes(key)) {
              nextSearch.append(key, value);
            }
          }
          console.log('nextSearch', nextSearch);
          history.replace({
            pathname: `${meetingId}/meeting`,
            search: nextSearch.toString(),
          });
        } catch (err: any) {
          console.warn(err);
          clearSessionStorage();
          elkReport({ type: '登录页初始化失败', logData: err });
          if ([404, 510001].includes(err.status)) {
            history.push({
              pathname: `/meet/${meetingId}/tips`,
              query: {
                type: TIPS_TYPE.NOT_FOUND,
              },
            });
          }
          // 5101 会议已失效 | 5102 未开启 web 入会开关
          if ([570004, 511003].includes(err.status)) {
            history.push({
              pathname: `/meet/${meetingId}/forbid`,
              query: {
                errorCode: err.status,
              },
            });
          }

          // 5101 会议已失效 | 5102 未开启 web 入会开关
          if ([5101, 5102, 570001, 510002].includes(err.status)) {
            history.push({
              pathname: `/meet/${meetingId}/tips`,
              query: {
                type: TIPS_TYPE.FINISHED,
              },
            });
          }

          if ([510006].includes(err.status)) {
            message.info({
              content: formatMessage({ id: 'meetingNeedPassword' }),
              noIcon: true,
            });
          }

          if ([510007].includes(err.status)) {
            message.info({
              content: formatMessage({ id: 'errorPassword' }),
              noIcon: true,
            });
          }
          setBtnLoading(false);
        }
      },
      [meetingId, user, meeting, currentAvatarId, formatMessage, firstMemberNotifyOn, isGuest]
    );

    return (
      <div className={Style['popo-meeting-ready-wrap']}>
        {!window.isMobile && (
          <FeedbackPanelPopover type="ready" placement="topRight">
            <div className={Style['feedback-btn']}>
              <Icon name="icon-feedback" size={24} className={Style.feedbackicon} />
            </div>
          </FeedbackPanelPopover>
        )}
        {window.isMobile ? (
          <ReadyH5
            meetingId={meetingId}
            mustCheck={mustCheck}
            checkMIC={checkMIC}
            onMicSwitchChange={onMicSwitchChange}
            isGuest={isGuest}
          />
        ) : (
          <LoginInput
            meetingId={meetingId}
            isMICChecking={isMICChecking}
            mustCheck={mustCheck}
            checkMIC={checkMIC}
            onMicSwitchChange={onMicSwitchChange}
            btnLoading={btnLoading}
            setFirstMemberNotifyOn={setFirstMemberNotifyOn}
            onInputChange={onInputChange}
            joinMeeting={joinMeeting}
          ></LoginInput>
        )}
        {isMICChecking && (
          <Check
            onClose={handleCloseMICCheck}
            mustCheck={mustCheck}
            joinMeeting={() => {
              !isAudioOpen && RTCInstance?.closeMic();
              joinMeeting(inputName);
            }}
            joinLoading={btnLoading}
          />
        )}
      </div>
    );
  })
);
export default Ready;
